import { memo, type MouseEvent, type ReactNode, type Ref } from 'react';
import cn from 'classnames';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';
import RatingStars from '_components/Product/ProductRatingStars';
import type { Swatch } from '@hooks/useHitSwatches';
import PlpColourPicker from '_components/ColourPicker/PlpColourPicker';
import Tag from '_components/UI/Tag';
import { DisplayTag } from '_components/Product/ProductMetaInfo/ProductMetaTags/ProductMetaTags';
import BuyCanadian from '_components/BuyCanadian';
import ProductComparisonCheckbox from '_components/Product/ProductComparisonCheckbox';
import MemberPricingDealsBanner from '@components/product/MemberPricingDealsBanner';

import styles from './ProductCard.module.scss';

/** Also fed to `PlpColourPicker` so hover-preload targets this exact image. */
const PRODUCT_CARD_IMAGE_SIZES =
  '(min-width: 1260px) calc(25vw - 56px), (min-width: 1128px) calc(25vw - 48px), (min-width: 1000px) calc(33.33vw - 51px), (min-width: 767px) calc(50vw - 56px), (min-width: 600px) calc(33.33vw - 43px), (min-width: 374px) calc(50vw - 48px), calc(100vw - 64px)';

export interface ProductCardProps {
  productImage: string;
  brand?: string;
  displayName: string;
  rating?: string;
  reviews?: string;
  guestDisplayPrice?: string;
  productUrl: string;
  displayPrice: string;
  salePrice?: string;
  /**
   * Discount % shown in the "X% off" badge. Algolia callers (Hit,
   * PlpCrossLinkingCard) should always pass this, sourced from the
   * BigCommerce promotion badge (`badge`/`badgeGuest.discount_percentage`)
   * rather than derived from `displayPrice`/`salePrice` — those are
   * formatted strings that can be a price *range* (e.g. "$15.16 - $31.96")
   * when a colour spans multiple sizes, and stripping non-numeric characters
   * from a range before parsing it as a single number silently corrupts the
   * result (ED-503). When omitted (the Algonomy-backed recommendation
   * carousels in `_components/ProductStrip` have no badge to source from),
   * falls back to parsing displayPrice/salePrice — Algonomy also sends price
   * *ranges* (e.g. "$18.95-39.95"), so that parse reads only the leading
   * number of each string rather than the whole thing.
   */
  discountPercentage?: number;
  /**
   * True when the badge's percentage is one representative variant's own
   * discount but other variants in the same style are discounted by a
   * different amount (BC's `multi_discount` flag) — the badge reads "up to
   * X% off" instead of a flat "X% off" so it doesn't overclaim.
   */
  multiDiscount?: boolean;
  /**
   * BC promotion badge `code` (e.g. `'SALE'`, `'CLEARANCE'`) when that badge
   * is member-price-flavoured (`is_member_price`). When set, the discount
   * badge shows fixed copy ("Sale"/"Clearance") instead of a computed
   * percentage — mirrors the legacy `PromoBadge` component's `is_member_price`
   * special case. Any other code (or omitted) falls back to the percentage
   * badge from `discountPercentage`.
   */
  memberBadgeCode?: string;
  swatches?: (Swatch & { hexColours?: string[] })[];
  trackClick?: (e: MouseEvent<HTMLAnchorElement>) => void;
  priority?: boolean;
  tags?: DisplayTag[];
  isCanadianBrand?: boolean;
  isMadeInCanada?: boolean;
  productCode?: string;
  showMemberPricingDealsBanner?: boolean;
  hasGroupPriceDifference?: boolean;
  isOnSaleOrClearance?: boolean;
  /**
   * Render the member-vs-guest price comparison: the member price as the
   * current price with the guest price struck through beside it.
   */
  showMemberPriceComparison?: boolean;
  dealsBannerRef?: Ref<HTMLDivElement>;
  mini?: boolean;
  /**
   * Inverts the `mini` card surface to white, for strips that sit on a tinted
   * section background (e.g. the PLP cross-linking band on `--neutrals-100`).
   * No effect on the full PLP card.
   */
  onTint?: boolean;
  /** Rendered inside the image wrapper (PLP/search grid only — never on `mini`). */
  quickAdd?: ReactNode;
}

const ProductCard = ({
  productImage,
  brand,
  displayName,
  rating,
  reviews,
  productUrl,
  guestDisplayPrice,
  displayPrice,
  salePrice,
  discountPercentage,
  multiDiscount = false,
  memberBadgeCode,
  swatches,
  trackClick,
  priority = false,
  tags = [],
  isCanadianBrand = false,
  isMadeInCanada = false,
  productCode = '',
  showMemberPricingDealsBanner = false,
  hasGroupPriceDifference = false,
  isOnSaleOrClearance = false,
  showMemberPriceComparison = false,
  dealsBannerRef,
  mini = false,
  onTint = false,
  quickAdd,
}: ProductCardProps) => {
  const { t } = useTranslation('common');
  const base = displayName?.replace(/^"|"$/g, '') ?? '';
  const cleanName = (() => {
    if (!brand?.trim()) return base;
    const escaped = brand.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return base.replace(new RegExp(`^${escaped}\\s*`, 'i'), '') || base;
  })();
  const strippedPrice = parseInt(displayPrice?.replace?.(/\D/g, '') || '0');
  const displaySale = !!salePrice;

  // Reads only the *leading* number out of a formatted price string, so a
  // range like "$18.95-39.95" (Algonomy) or "$15.16 - $31.96" (Algolia)
  // yields its low end (18.95 / 15.16) rather than corrupting into a single
  // garbage number by concatenating both ends' digits together (ED-503).
  //
  // Must also handle fr-CA formatting, not just en: formatPrice() renders fr
  // prices with a comma as the *decimal* separator and a narrow no-break
  // space as the thousands separator (e.g. "1 999,00 $" for $1,999.00,
  // matching MiniProductCard's `parsePrice` below). Blindly stripping commas
  // (as if they were always an en thousands separator) destroys the decimal
  // point on fr prices and truncates anything >= $1,000 at the thousands
  // space, corrupting the discount % this same fix was meant to protect.
  const parseLeadingPrice = (value?: string) => {
    const cleaned = (value ?? '').replace(/[^\d.,-]/g, '');
    if (!cleaned) return 0;
    const hasComma = cleaned.includes(',');
    const hasDot = cleaned.includes('.');
    const normalized =
      hasComma && !hasDot
        ? cleaned.replace(',', '.')
        : cleaned.replace(/,/g, '');
    return parseFloat(normalized) || 0;
  };

  const parsedDisplayPrice = parseLeadingPrice(displayPrice);
  const parsedSalePrice = parseLeadingPrice(salePrice);

  // Non-Algolia callers (e.g. the Algonomy-backed recommendation carousels in
  // _components/ProductStrip) have no BC promo badge to source a percentage
  // from, so this derives one from their price strings instead — using only
  // the leading number of each (see parseLeadingPrice above) so a price
  // *range* doesn't produce a bogus discount. Only fall back to that parse
  // when discountPercentage is genuinely omitted (undefined), not when a
  // caller explicitly passes 0 - Algolia callers (Hit, PlpCrossLinkingCard)
  // always pass discountPercentage explicitly, sourced from the real BC
  // promo badge, so this fallback never runs for them.
  const fallbackDiscountPercent =
    displaySale && parsedDisplayPrice > 0
      ? Math.round((1 - parsedSalePrice / parsedDisplayPrice) * 100)
      : 0;
  const discountPercent =
    discountPercentage !== undefined
      ? Math.round(discountPercentage)
      : fallbackDiscountPercent;
  // A member-price-flavoured SALE/CLEARANCE badge shows fixed copy instead of
  // a percentage (legacy PromoBadge's is_member_price special case) — the
  // member discount isn't always expressed as a clean %, and the fixed
  // wording reads better for that context regardless.
  const memberBadgeTextKey =
    memberBadgeCode === 'CLEARANCE'
      ? 'productStrip.discountBadge.member.clearance'
      : memberBadgeCode === 'SALE'
      ? 'productStrip.discountBadge.member.sale'
      : null;

  const discountBadgeText = memberBadgeTextKey
    ? t(memberBadgeTextKey)
    : t(
        multiDiscount
          ? 'productStrip.discountBadge.upTo'
          : 'productStrip.discountBadge',
        { percent: discountPercent }
      );

  // The member-copy badge isn't gated on a positive percentage - a member
  // discount that isn't expressed as a clean % still needs its badge shown.
  const hasDiscountBadge = memberBadgeTextKey ? true : discountPercent > 0;

  // Compares the original formatted strings rather than parsedSalePrice/
  // parsedDisplayPrice: those only read each range's leading value, so two
  // different ranges sharing the same low end (e.g. "$15.16 - $31.96" vs
  // "$15.16 - $39.95") would parse equal and wrongly hide the crossed-out
  // original price.
  const showOriginalPrice =
    hasGroupPriceDifference ||
    (isOnSaleOrClearance && salePrice !== displayPrice);
  const originalPrice = hasGroupPriceDifference
    ? guestDisplayPrice
    : displayPrice;

  // Mini variant (ED-171): compact card used inside the mini ProductStrip on the
  // PDP. Link wraps the whole card; no tags, swatches, comparison, or deals banner.
  if (mini) {
    return (
      <article
        className={cn(styles.card, styles.mini, { [styles.onTint]: onTint })}
      >
        <a
          className={cn(styles.link, styles.mini)}
          href={productUrl}
          onClick={(e) => trackClick?.(e)}
        >
          <div className={cn(styles.imageWrapper, styles.mini)}>
            <div className={cn(styles.imagePad, styles.mini)}>
              <Image
                src={productImage}
                alt=""
                fill
                sizes="248px"
                className={styles.image}
                priority={priority}
                loading={priority ? undefined : 'eager'}
              />
            </div>
          </div>
          <div className={cn(styles.info, styles.mini)}>
            <div className={styles.productInfo}>
              {brand && <p className={styles.brandName}>{brand}</p>}
              <p className={cn(styles.productName, styles.mini)}>{cleanName}</p>
            </div>
            {strippedPrice > 0 && (
              <div className={styles.price}>
                <div className={styles.priceGroup}>
                  {displaySale ? (
                    <>
                      <span className={styles.currentPrice}>{salePrice}</span>
                      <span className={styles.originalPrice}>
                        {displayPrice}
                      </span>
                    </>
                  ) : (
                    <span>{displayPrice}</span>
                  )}
                </div>
                {displaySale && hasDiscountBadge && (
                  <span className={styles.discountBadge}>
                    {discountBadgeText}
                  </span>
                )}
              </div>
            )}
            {(rating || reviews) && (
              <div className={styles.ratings}>
                {rating && <RatingStars rating={parseFloat(rating)} />}
                {reviews && (
                  <span className={styles.reviewCount}>({reviews})</span>
                )}
              </div>
            )}
          </div>
        </a>
      </article>
    );
  }

  const hasTags = tags?.length > 0;

  return (
    <article className={styles.card}>
      <div className={styles.imageWrapper}>
        <div className={styles.imagePad}>
          <Image
            src={productImage}
            alt=""
            fill
            // Rendered image width = (100vw - 2×page gutter) × grid column
            // fraction - 2×(.searchHitsItem.newHit padding) - 2×.imagePad's
            // own 12px inset. Grid fractions/padding: Hits.module.scss
            // `.searchHitsItem`/`.newHit`. Page gutter: 16px, or 32px at
            // ≥1260px under FEATURE_FLAG_PLP_FULL_WIDTH (Container.module.scss
            // `.fullWidthGutter`) — assumes that flag's layout, the PLP/search
            // target state, since a static `sizes` can't branch on it.
            sizes={PRODUCT_CARD_IMAGE_SIZES}
            className={styles.image}
            priority={priority}
            loading={priority ? undefined : 'eager'}
          />
        </div>
        <div className={styles.buyCanadianWrapper}>
          {(isCanadianBrand || isMadeInCanada) && (
            <BuyCanadian
              hit={true}
              isCanadianBrand={isCanadianBrand && !isMadeInCanada}
              isMadeInCanada={isMadeInCanada}
            />
          )}
        </div>
        {quickAdd}
      </div>
      {hasTags && (
        <div className={styles.tagsContainer}>
          {tags.slice(0, 2).map((tag) => (
            <Tag
              key={tag?.name}
              size="small"
              color={tag?.color || 'gray'}
              onDarkBg
            >
              {tag?.name}
            </Tag>
          ))}
        </div>
      )}

      <div className={styles.info}>
        <div className={styles.swatches}>
          <PlpColourPicker
            swatches={swatches ?? []}
            preloadImageSizes={PRODUCT_CARD_IMAGE_SIZES}
          />
        </div>
        <div className={styles.productInfo}>
          {brand && <p className={styles.brandName}>{brand}</p>}
          <p className={styles.productName}>{cleanName}</p>
        </div>
        {strippedPrice > 0 && (
          <div className={styles.price}>
            <div className={styles.priceGroup}>
              {showMemberPriceComparison ? (
                <>
                  <span className={cn(styles.currentPrice, styles.groupPrice)}>
                    {displaySale ? salePrice : displayPrice}
                  </span>
                  {guestDisplayPrice && (
                    <span className={styles.originalPrice}>
                      {guestDisplayPrice}
                    </span>
                  )}
                </>
              ) : displaySale ? (
                <>
                  <span
                    className={cn(styles.currentPrice, {
                      [styles.groupPrice]: hasGroupPriceDifference,
                    })}
                  >
                    {salePrice}
                  </span>
                  {showOriginalPrice && (
                    <span className={styles.originalPrice}>
                      {originalPrice}
                    </span>
                  )}
                </>
              ) : (
                <span>{displayPrice}</span>
              )}
            </div>
            {isOnSaleOrClearance && hasDiscountBadge && (
              <span className={styles.discountBadge}>{discountBadgeText}</span>
            )}
          </div>
        )}
        <div className={styles.ratings}>
          <RatingStars
            rating={rating ? parseFloat(rating) : 0}
            starSize="ExtraSmall"
          />
          {Number(rating) > 0 ? (
            <span className={styles.ratingValue}>{rating}</span>
          ) : (
            <span className={styles.reviewCount}>({reviews || 0})</span>
          )}
        </div>
      </div>
      {showMemberPricingDealsBanner && (
        <div className={styles.dealsBannerContainer} ref={dealsBannerRef}>
          <MemberPricingDealsBanner isPlp={true} />
        </div>
      )}

      <div className={styles.comparisonCheckbox}>
        <ProductComparisonCheckbox product={{ code: productCode }} />
      </div>

      <a
        className={styles.link}
        href={productUrl}
        onClick={(e) => trackClick?.(e)}
        aria-label={[brand, cleanName].filter(Boolean).join(' ')}
      />
    </article>
  );
};

export default memo(ProductCard);
